import { PP_DEFAULT_MOCK_PATIENT_ID } from '../patients/config';
import { resolvePatientMockBundleKey } from '../patients/utils';
import { PP_FORMS_BY_PATIENT } from './mockData';
import type {
  PfAssignmentStatus,
  PfLifecycleStage,
  PfPatientFormRecord,
  PfPatientFormsBundle,
  PfSyncStatus,
} from './types';

const EMPTY_BUNDLE: PfPatientFormsBundle = {
  aiSummary: '',
  forms: [],
  stats: { completed: 0, open: 0, needsReview: 0, total: 0 },
};

export function getPatientFormsBundle(patientId: string): PfPatientFormsBundle {
  const key = resolvePatientMockBundleKey(patientId);
  return (
    PP_FORMS_BY_PATIENT[key] ?? PP_FORMS_BY_PATIENT[PP_DEFAULT_MOCK_PATIENT_ID] ?? EMPTY_BUNDLE
  );
}

/** The ordered pipeline the stepper renders. */
export const PF_STAGES: readonly PfLifecycleStage[] = [
  'sent',
  'filling',
  'submitted',
  'closed',
  'synced',
] as const;

export const PF_STAGE_LABEL: Record<PfLifecycleStage, string> = {
  sent: 'Sent',
  filling: 'Filling out',
  submitted: 'Submitted',
  closed: 'Closed',
  synced: 'Synced',
};

export interface PfStageState {
  /** Index into PF_STAGES the form has reached (-1 before it is sent). */
  index: number;
  stage: PfLifecycleStage | null;
  /** A terminal off-pipeline outcome, if any. */
  terminal: 'incomplete' | null;
  /** True once the form has fully synced. */
  done: boolean;
}

/**
 * Collapse the two live axes into the current pipeline position. The submission
 * axis mirrors the assignment axis in the mock, so assignment + sync is enough.
 */
export function pfFormStage(form: PfPatientFormRecord): PfStageState {
  const { assignmentStatus, syncStatus } = form;

  if (assignmentStatus === 'incomplete') {
    return { index: 2, stage: 'submitted', terminal: 'incomplete', done: false };
  }
  if (assignmentStatus === 'cancelled' || assignmentStatus === 'expired') {
    return { index: 0, stage: 'sent', terminal: 'incomplete', done: false };
  }
  if (assignmentStatus === 'not_sent') {
    return { index: -1, stage: null, terminal: null, done: false };
  }
  if (assignmentStatus === 'pending') {
    return { index: 0, stage: 'sent', terminal: null, done: false };
  }
  if (assignmentStatus === 'in_progress') {
    return { index: 1, stage: 'filling', terminal: null, done: false };
  }
  if (assignmentStatus === 'submitted') {
    return { index: 2, stage: 'submitted', terminal: null, done: false };
  }
  // approved = closed by staff — synced or not-yet-synced.
  if (syncStatus === 'synced') {
    return { index: 4, stage: 'synced', terminal: null, done: true };
  }
  return { index: 3, stage: 'closed', terminal: null, done: false };
}

/** The headline pill label for a lifecycle position (shared by the tab and the queue). */
export function pfLifecycleLabel(
  assignmentStatus: PfAssignmentStatus,
  syncStatus: PfSyncStatus,
): string {
  switch (assignmentStatus) {
    case 'not_sent':
      return 'Not sent';
    case 'pending':
      return 'Sent · awaiting patient';
    case 'in_progress':
      return 'Patient filling out';
    case 'submitted':
      return 'Needs review';
    case 'incomplete':
      return 'Closed · incomplete';
    case 'cancelled':
      return 'Cancelled';
    case 'expired':
      return 'Link expired';
    case 'approved':
      return syncStatus === 'synced' ? 'Complete · synced' : 'Closed';
  }
}

export function pfAssignmentLabel(form: PfPatientFormRecord): string {
  return pfLifecycleLabel(form.assignmentStatus, form.syncStatus);
}

export const PF_SYNC_LABEL: Record<PfSyncStatus, string> = {
  not_started: 'Not synced yet',
  synced: 'Synced to PMS',
};

/** Headline-pill colour for a lifecycle position (shared by the tab and the queue). */
export function pfAssignmentTone(
  assignmentStatus: PfAssignmentStatus,
  syncStatus: PfSyncStatus,
): string {
  switch (assignmentStatus) {
    case 'approved':
      return syncStatus === 'synced' ? 'var(--success)' : 'var(--ai-3)';
    case 'submitted':
      return 'var(--gold)';
    case 'in_progress':
      return 'var(--ai-3)';
    case 'pending':
      return 'var(--info)';
    case 'incomplete':
    case 'cancelled':
    case 'expired':
      return 'var(--terracotta)';
    default:
      return 'var(--ink-4)';
  }
}

export function pfSyncTone(syncStatus: PfSyncStatus): string {
  return syncStatus === 'synced' ? 'var(--success)' : 'var(--ink-4)';
}

/** Only approved forms carry a meaningful sync chip. */
export function pfShowsSync(status: PfAssignmentStatus): boolean {
  return status === 'approved';
}

export function pfFormsStats(forms: PfPatientFormRecord[]): PfPatientFormsBundle['stats'] {
  let completed = 0;
  let needsReview = 0;
  let open = 0;
  for (const f of forms) {
    const stage = pfFormStage(f);
    if (stage.done) completed += 1;
    else if (f.assignmentStatus === 'submitted') {
      needsReview += 1;
      open += 1;
    } else if (
      f.assignmentStatus === 'pending' ||
      f.assignmentStatus === 'in_progress' ||
      f.assignmentStatus === 'approved'
    ) {
      open += 1;
    }
  }
  return { completed, open, needsReview, total: forms.length };
}
